import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
} from '@mui/material'
import CallSplitIcon from '@mui/icons-material/CallSplit'
import LockIcon from '@mui/icons-material/Lock'
import MicExternalOnIcon from '@mui/icons-material/MicExternalOn'
import DownloadIcon from '@mui/icons-material/Download'

const FEATURES = [
  {
    icon: <CallSplitIcon color="primary" />,
    title: 'Split vocals and instrumental',
    body: 'Drop in any song and get two stems back: a clean instrumental (backing track) and the isolated vocals, both downloadable as WAV.',
  },
  {
    icon: <LockIcon color="primary" />,
    title: 'Everything stays in your browser',
    body: 'There is no backend. Your audio is decoded, separated and played back entirely on this device — nothing is ever uploaded, and nothing leaves the browser.',
  },
  {
    icon: <MicExternalOnIcon color="primary" />,
    title: 'Instant karaoke mode',
    body: 'You don’t have to wait for the full song to finish processing. Playback starts within seconds, so you can press play and sing along karaoke-style while the rest is still separating.',
  },
]

/** Opt-in landing page. The separation model (~35 MB) is deliberately NOT
 * loaded until the user clicks Start, so people on mobile data don't pay for
 * a download they never asked for. */
export function StartPage({ onStart }: { onStart: () => void }) {
  return (
    <Container maxWidth="sm" sx={{ py: { xs: 5, sm: 8 } }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }} gutterBottom>
        Vocal Remover
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
        Separate any song into vocals and instrumental — right here, right on
        your device.
      </Typography>

      <Stack spacing={2} sx={{ mb: 4 }}>
        {FEATURES.map((f) => (
          <Card key={f.title} variant="outlined">
            <CardContent sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ pt: 0.3 }}>{f.icon}</Box>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{f.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {f.body}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Button variant="contained" size="large" fullWidth onClick={onStart}>
        Start
      </Button>
      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 1.5, color: 'text.secondary', alignItems: 'center', justifyContent: 'center' }}
      >
        <DownloadIcon fontSize="small" />
        <Typography variant="body2">
          Clicking Start downloads the separation model (~35 MB) to your
          browser. On mobile data, you may want to wait for Wi-Fi.
        </Typography>
      </Stack>
    </Container>
  )
}
